import React from "react";
import {
  faCalendarAlt,
  faFileAlt,
  faLandmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Chip, Divider, Grid, Paper, Typography } from "@mui/material";
import { useSelector } from "react-redux";

// Custom Timeline components
const TimelineContainer = ({ children }) => {
  if (!children) return null;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 1.5,
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        "&:before": {
          content: '""',
          position: "absolute",
          left: 20,
          top: 0,
          bottom: 0,
          width: 2,
          background:
            "linear-gradient(to bottom, #2563EB 0%, #3B82F6 50%, #60A5FA 100%)",
          borderRadius: "2px",
          opacity: 0.9,
          boxShadow: "0 0 8px rgba(37, 99, 235, 0.2)",
        },
      }}
    >
      {children}
    </Box>
  );
};

const TimelineEvent = ({ title, date, isLast = false, color = "primary" }) => {
  const getColorStyles = (color) => {
    const colorMap = {
      info: {
        bg: "#F8FAFC",
        border: "#E2E8F0",
        text: "#1E40AF",
        dot: "#3B82F6",
        dateBg: "#EFF6FF",
        dateText: "#1E40AF",
        hoverBg: "#EFF6FF",
        glow: "rgba(59, 130, 246, 0.15)",
      },
      warning: {
        bg: "#F8FAFC",
        border: "#E2E8F0",
        text: "#92400E",
        dot: "#F59E0B",
        dateBg: "#FFFBEB",
        dateText: "#92400E",
        hoverBg: "#FFFBEB",
        glow: "rgba(245, 158, 11, 0.15)",
      },
      success: {
        bg: "#F8FAFC",
        border: "#E2E8F0",
        text: "#065F46",
        dot: "#10B981",
        dateBg: "#ECFDF5",
        dateText: "#065F46",
        hoverBg: "#ECFDF5",
        glow: "rgba(16, 185, 129, 0.15)",
      },
      primary: {
        bg: "#F8FAFC",
        border: "#E2E8F0",
        text: "#1E40AF",
        dot: "#3B82F6",
        dateBg: "#EFF6FF",
        dateText: "#1E40AF",
        hoverBg: "#EFF6FF",
        glow: "rgba(59, 130, 246, 0.15)",
      },
    };
    return colorMap[color] || colorMap.primary;
  };

  const colors = getColorStyles(color);

  return (
    <Box
      sx={{
        position: "relative",
        pl: 3.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateX(4px)",
          "& .timeline-dot": {
            transform: "scale(1.2)",
            boxShadow: `0 0 0 4px ${colors.glow}`,
          },
          "& .timeline-content": {
            borderColor: colors.dot,
            bgcolor: colors.hoverBg,
            boxShadow: `0 4px 12px ${colors.glow}`,
          },
        },
      }}
    >
      <Box
        className="timeline-dot"
        sx={{
          position: "absolute",
          left: 1,
          top: 12,
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: colors.dot,
          border: "2px solid #FFFFFF",
          boxShadow: "0 0 0 1px #E2E8F0",
          zIndex: 1,
          flexShrink: 0,
          transition: "all 0.3s ease",
        }}
      />
      <Box
        className="timeline-content"
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
          p: 1.25,
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `linear-gradient(45deg, ${colors.glow} 0%, transparent 100%)`,
            opacity: 0,
            transition: "opacity 0.3s ease",
          },
          "&:hover:before": {
            opacity: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: colors.text,
              fontSize: "0.8125rem",
              flex: 1,
              minWidth: 0,
              letterSpacing: "-0.01em",
            }}
          >
            {title || "Untitled Event"}
          </Typography>
          <Box
            sx={{
              bgcolor: colors.dateBg,
              borderRadius: "4px",
              px: 1,
              py: 0.25,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              border: `1px solid ${colors.border}`,
              flexShrink: 0,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 2px 4px ${colors.glow}`,
              },
            }}
          >
            <FontAwesomeIcon
              icon={faCalendarAlt}
              style={{
                fontSize: "0.65rem",
                color: colors.dateText,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: colors.dateText,
                fontSize: "0.65rem",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
            >
              {date || "Date not available"}
            </Typography>
          </Box>
        </Box>
        {!isLast && (
          <Box
            sx={{
              position: "absolute",
              left: 20,
              top: "100%",
              bottom: -12,
              width: 2,
              bgcolor: "#E2E8F0",
              borderRadius: "2px",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(to bottom, #2563EB, #3B82F6)",
                boxShadow: "0 0 8px rgba(37, 99, 235, 0.2)",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const timelineEvents = [
  {
    title: "Case Filed",
    date: "24 Jul 2007",
    color: "info",
  },
  {
    title: "First Hearing",
    date: "15 Aug 2007",
    color: "warning",
  },
  {
    title: "Judgment Delivered",
    date: "30 Sep 2007",
    color: "success",
  },
];

const CaseDetailOverview = () => {
  const caseDetail = useSelector((state) => state.caseDetail);
  const { caseItem } = caseDetail;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        paddingBottom: 2,
        overflowY: "visible",
      }}
    >
      {/* Case Timeline Section */}
      {/* <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
            mb: 1.5,
            px: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#EFF6FF",
                borderRadius: "6px",
                color: "#2563EB",
                boxShadow: "0 1px 2px rgba(37, 99, 235, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                  boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
                },
              }}
            >
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ fontSize: "0.75rem" }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "#1E293B",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                }}
              >
                Case Timeline
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748B",
                  fontSize: "0.7rem",
                  display: "block",
                }}
              >
                Chronological sequence of case events
              </Typography>
            </Box>
          </Box>
          <Chip
            size="small"
            label={`${timelineEvents?.length || 0} Events`}
            sx={{
              height: "20px",
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#EFF6FF",
                borderColor: "#2563EB",
                transform: "translateY(-1px)",
              },
              "& .MuiChip-label": {
                px: 1,
                fontSize: "0.7rem",
                fontWeight: 500,
                color: "#64748B",
              },
            }}
          />
        </Box>
        <TimelineContainer>
          {Array.isArray(timelineEvents) && timelineEvents.length > 0 ? (
            timelineEvents.map((event, index) => (
              <TimelineEvent
                key={index}
                title={event?.title}
                date={event?.date}
                color={event?.color}
                isLast={index === timelineEvents.length - 1}
              />
            ))
          ) : (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                No timeline events available
              </Typography>
            </Box>
          )}
        </TimelineContainer>
      </Box> */}

      {/* Case Summary Section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            mb: 2,
            px: 1,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#F5F3FF",
              borderRadius: "8px",
              color: "#7C3AED",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.1)",
                boxShadow: "0 2px 8px rgba(124, 58, 237, 0.2)",
              },
            }}
          >
            <FontAwesomeIcon
              icon={faFileAlt}
              style={{ fontSize: "0.875rem" }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "#1E293B",
              letterSpacing: "-0.01em",
            }}
          >
            Case Summary
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            transition: "all 0.3s ease",
            overflowY: "auto",
            "&:hover": {
              borderColor: "#7C3AED",
              boxShadow: "0 4px 12px rgba(124, 58, 237, 0.08)",
              transform: "translateY(-1px)",
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#334155",
              lineHeight: 1.6,
              fontSize: "0.8125rem",
              fontWeight: 400,
              letterSpacing: "0.01em",
              "&::first-letter": {
                fontSize: "1.2em",
                fontWeight: 600,
                color: "#7C3AED",
                paddingRight: "0.1em",
              },
            }}
          >
            {caseItem?.Short_Summary || "No case summary available."}
          </Typography>
        </Paper>
      </Box>

      {/* Court's Decision Section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            px: 1,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#ECFDF5",
              borderRadius: "8px",
              color: "#059669",
            }}
          >
            <FontAwesomeIcon
              icon={faLandmark}
              style={{ fontSize: "0.875rem" }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "#1E293B",
              letterSpacing: "-0.01em",
            }}
          >
            Court's Decision
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#059669",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#1E293B",
                fontSize: "0.875rem",
                fontWeight: 600,
                mb: 1,
              }}
            >
              Result
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#334155",
                lineHeight: 1.6,
                fontSize: "0.875rem",
              }}
            >
              {caseItem?.Result || "No case result available."}
            </Typography>
          </Box>
          {/* <Divider sx={{ my: 2, borderColor: "#E2E8F0" }} /> */}
          {/* Related cases commented out temporarily */}
          {/* <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#1E293B",
                fontSize: "0.875rem",
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Related Cases
            </Typography>
            <Grid container spacing={1.5}>
              {[1, 2, 3].map((_, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      boxSizing: "border-box",
                      bgcolor: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: "10px",
                      height: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#059669",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#1E293B",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Sample Related Case {index + 1}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748B",
                        fontSize: "0.7rem",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Citation: 2023 SC 123
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#475569",
                        fontSize: "0.75rem",
                        lineHeight: 1.5,
                      }}
                    >
                      Brief description of the related case and its relevance to
                      the current case...
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box> */}
        </Paper>
      </Box>
    </Box>
  );
};

export default CaseDetailOverview;
